<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { translateEntityProperty } from '@/utils/helper.ts';
import QuantityForm from './components/QuantityForm.vue';
import { useLoading } from '@/utils/useLoading';
import { onMounted } from 'vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { NumericQuantityDtoTypeEnum, type NumericQuantityDto } from '@/api/generator';
import { useToken } from '@/utils/tokens/tokens';
import { useRouter } from 'vue-router';
import { toCreateUpdateNumericQuantityDto } from './mappings';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';

const router = useRouter();

export interface QuantityEditViewProps {
  id: string;
}

const props = defineProps<QuantityEditViewProps>();

const { action: loadQuantity, loading } = useLoading(async () => {
  const { result } = await api.numericQuantitiesApi.getNumericQuantity(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );
  if (result) {
    resetForm({ values: result });
  }
}, true);

const token = useToken({
  id: props.id,
  $type:
    NumericQuantityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoNumericDtoNumericQuantityDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(loadQuantity);

token.on.inUse(() => {
  router.push({ name: routenames.quantities.overview });
});

const { form } = useVeeForm<NumericQuantityDto>({
  schemaName: 'NumericQuantityDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((numericQuantityDto, errorHandlers) =>
      api.numericQuantitiesApi.validateNumericQuantity(
        {
          createUpdateNumericQuantityDto: toCreateUpdateNumericQuantityDto(numericQuantityDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values, resetForm } = form;

const submit = handleSubmit(
  async (numericQuantityDto) => {
    const { success } = await api.numericQuantitiesApi.updateNumericQuantity(
      {
        id: numericQuantityDto.id,
        createUpdateNumericQuantityDto: toCreateUpdateNumericQuantityDto(numericQuantityDto),
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.quantities.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle
          :subtitle="$t('Quantity_one')"
          :title="translateEntityProperty('name', values)"
          :titleId="values.iuid"
        />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.quantities.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <QuantityForm />
    </BasePage>
  </form>
</template>
